// fullFaceBotoxData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const dysportBotox: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} = {

    "en": {
        "url": "dysport-botox",
        "meta": {
            "title": "Dysport Botox Treatment in Dubai | Elite Body Home Polyclinic",
            "description": "Professional Dysport Botox treatments in Dubai for smoother, younger-looking skin. Reduce wrinkles and fine lines with this FDA-approved treatment.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/dysport-botox/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Dysport Botox Treatment in Dubai | Elite Body Home Polyclinic",
                "description": "Professional Dysport Botox treatments in Dubai for smoother, younger-looking skin. Reduce wrinkles and fine lines with this FDA-approved treatment.",
                "url": "https://elitebodyhome.com/services/dysport-botox/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2024-12-30T10:41:36+00:00"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Dysport Botox Treatment in Dubai | Elite Body Home Polyclinic",
                "description": "Professional Dysport Botox treatments in Dubai for smoother, younger-looking skin. Reduce wrinkles and fine lines with this FDA-approved treatment.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-57.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Dysport Botox"],
        "hero": {
            "title": "Dysport Botox Treatment in Dubai",
            "subtitle": "Smoother, Younger-Looking Skin",
            "description": "We at Elite Body Home deliver Dysport Botox, a well-liked anti-wrinkle procedure that makes skin smoother and younger by lessening the visibility of wrinkles and fine lines. Dysport is ideal for people who want a rejuvenated appearance with no downtime because of its faster outcomes and more natural appearance."
        },
        "howItWorks": {
            "title": "What is Dysport Botox?",
            "description": "Botox and Dysport are two well-liked nonsurgical wrinkle-reduction procedures. Although they both target the muscles that result in fine lines, their approved applications differ in a few ways. The wrinkles between the eyebrows are known as glabellar lines, and Dysport is particularly licensed to treat these. Contrarily, Botox is authorized for a broader range of regions, such as glabellar lines, crow's feet (lines surrounding the eyes), and forehead wrinkles. Although their focal regions are slightly different, both treatments relax muscles to produce smoother skin."
        },
        "procedureSteps": {
            "heading": "Steps For Dysport Botox Treatment Procedure",
            "para": "By relaxing specific muscles, the Dysport Botox method is a non-invasive way to temporarily lessen the appearance of wrinkles on the face. The procedure smoothes out the skin on top by injecting a tiny quantity of botulinum toxin (Dysport) into particular muscles to stop them from contracting.",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "A qualified practitioner will assess your facial anatomy and discuss your desired outcomes.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "Injection Sites",
                    "desc": "Common areas for Dysport injections include the forehead, around the eyes, and frown lines between the eyebrows.",
                    "icon": "fa-solid fa-map-marker-alt"
                },
                {
                    "title": "Duration",
                    "desc": "The treatment typically takes 10–20 minutes, with minimal discomfort due to the use of fine needles.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "Aftercare",
                    "desc": "Mild swelling or redness may occur, but these effects usually subside within a few hours.",
                    "icon": "fa-solid fa-heart-pulse"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Dysport Botox",
            "subheading": "",
            "para": "Due to its many advantages, Dysport Botox is a well-liked option for people who want to revitalize their skin without having surgery. Dysport is the preferred choice for treating wrinkles for the following reasons:",
            "items": [
                {
                    "title": "Fast Outcomes",
                    "desc": "After the surgery, effects may be observed as early as two to three days later.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Non-intrusive",
                    "desc": "Anesthesia is not necessary for Dysport injections, which are non-surgical.",
                    "icon": "fa-solid fa-hand-holding-medical"
                },
                {
                    "title": "Very little downtime",
                    "desc": "Following therapy, patients can resume their regular activities right away.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "Organic Look",
                    "desc": "Dysport prevents the 'frozen' look by smoothing wrinkles naturally and subtly.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Durable Outcomes",
                    "desc": "Dysport is a practical choice for long-term skin care because its effects usually last three to four months.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Increases Confidence",
                    "desc": "Full face Botox makes you feel more rejuvenated and confident by enhancing your facial features.",
                    "icon": "fa-solid fa-heart-circle-plus"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "Finding out if you are a good candidate for Dysport or Botox treatment is crucial. In general, healthy people between the ages of 18 and 65 may want to think about this treatment.",
            "items": [
                {
                    "title": "Individuals with dynamic wrinkles",
                    "desc": "Frequent facial movements, such as frowning or squinting, cause these wrinkles.",
                    "icon": "fa-solid fa-user-check"
                },
                {
                    "title": "People looking for a non-surgical remedy",
                    "desc": "For people who wish to avoid the dangers and recuperation time involved with cosmetic surgery, Dysport is ideal.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Patients who have reasonable expectations",
                    "desc": "Even while Dysport significantly reduces wrinkles, not all of them may be gone, particularly deep-set lines.",
                    "icon": "fa-solid fa-scale-balanced"
                },
                {
                    "title": "Not nursing or pregnant",
                    "desc": "Women who are pregnant or nursing shouldn't use Dysport.",
                    "icon": "fa-solid fa-baby-carriage"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation for Dysport Botox",
            "subheading": "",
            "para": "Being well-prepared is essential to getting the most out of your Dysport Botox treatment. The following actions should be taken before to the procedure:",
            "items": [
                {
                    "title": "Steer clear of blood thinners",
                    "desc": "To reduce bruising, avoid taking aspirin, ibuprofen, or other blood-thinning drugs at least one week before the procedure.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Keep hydrated",
                    "desc": "To keep your skin hydrated and healthy, drink a lot of water in the days before your visit.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Limit alcohol consumption",
                    "desc": "To lower the chance of bruising and swelling, abstain from alcohol for 24 hours before your treatment.",
                    "icon": "fa-solid fa-wine-glass-empty"
                },
                {
                    "title": "Notify your practitioner",
                    "desc": "Some medications or supplements may interfere with the process, so let your provider know about them.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "Come with Clean Skin",
                    "desc": "On the day of your appointment, show up with a clean face devoid of lotions or makeup.",
                    "icon": "fa-solid fa-soap"
                }
            ]
        },
        "results": {
            "heading": "Results of Dysport and Botox",
            "subheading": "",
            "para": "Dysport and Botox reduce the appearance of dynamic wrinkles, giving skin a smoother, more natural appearance. Following the procedure, patients might seem younger and more rejuvenated with little interruption to their everyday routines.",
            "items": [
                {
                    "title": "Smoother Skin",
                    "desc": "There is a noticeable softening of wrinkles and fine lines.",
                    "icon": "fa-solid fa-face-grin-stars"
                },
                {
                    "title": "Organic Look",
                    "desc": "Without appearing excessive, the loosened muscles give off a more youthful appearance.",
                    "icon": "fa-solid fa-check-double"
                },
                {
                    "title": "Prolonged Impacts",
                    "desc": "Since results usually last three to four months, it's a practical choice for keeping skin wrinkle-free and smooth.",
                    "icon": "fa-regular fa-hourglass-half"
                },
                {
                    "title": "Enhanced Self-Belief",
                    "desc": "After the procedure, many patients say they feel more assured and content with the way they look.",
                    "icon": "fa-solid fa-heart-circle-plus"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Why Choose Elite Body Home for Dysport Botox?",
            "subheading": "",
            "para": "At Elite Body Home, we specialize in delivering natural-looking Dysport Botox results tailored to your unique facial structure. Our certified practitioners use premium products and precise techniques to ensure optimal outcomes with minimal downtime.",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "If you're considering Dysport Treatment in Dubai, here are answers to common questions about the procedure and results.",
            "items": [
                {
                    "question": "How long does it take to see results?",
                    "answer": "Results can be seen as early as 2-3 days post-treatment."
                },
                {
                    "question": "Is Dysport safe?",
                    "answer": "Yes, Dysport is FDA-approved and has been used safely in aesthetic treatments for years."
                },
                {
                    "question": "How often should I get Dysport injections?",
                    "answer": "Most patients opt for treatments every 4-6 months to maintain their results."
                },
                {
                    "question": "What areas can be treated with Dysport?",
                    "answer": "Dysport is primarily used for frown lines between eyebrows, forehead lines, and crow's feet."
                }
            ]
        },
        "treatmentAreas1": [
            {
                "ar": "",
                "title": "Forehead Lines",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Forehead-Lines.jpg"
            },
            {
                "ar": "",
                "title": "Frown Lines",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Frown-Lines.jpg"
            }
        ],
        "treatmentAreas2": [
            {
                "ar": "",
                "title": "Crow's Feet",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Crows-Feet.jpg"
            }
        ]
    },
      
    "shared": {
        "images": {
            "introductionImage": "",
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/ebh-services-uae-48-scaled.jpg",
            "beforeAfter": [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Before-After-Service-Images-7.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Dysport-Botox-Treatment-in-Dubai-2.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Dysport-Botox-Treatment-in-Dubai-1.jpg"
            ]
        },
        "product": {
            "id": 1,
            "title": "Dysport Botox",
            "description": "Get Dysport Botox injections in Dubai for natural, youthful results. Smooth wrinkles between your eyebrows with safe, effective treatments by experts.",
            "price": 199
        }
    },

    "ar": {
        "url": "dysport-botox",
        "meta": {
            "title": "حقن ديسبورت في دبي | عيادات ايليت بودي هوم",
            "description": "احصل على حقن ديسبورت في دبي لنتائج طبيعية وشبابية. تنعيم التجاعيد بين الحاجبين بعلاجات آمنة وفعالة من قبل خبراء.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/dysport-botox/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "حقن ديسبورت في دبي | عيادات ايليت بودي هوم",
                "description": "احصل على حقن ديسبورت في دبي لنتائج طبيعية وشبابية. تنعيم التجاعيد بين الحاجبين بعلاجات آمنة وفعالة من قبل خبراء.",
                "url": "https://elitebodyhome.com/dysport-botox/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2024-12-30T10:41:36+00:00"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "حقن ديسبورت في دبي | عيادات ايليت بودي هوم",
                "description": "احصل على حقن ديسبورت في دبي لنتائج طبيعية وشبابية. تنعيم التجاعيد بين الحاجبين بعلاجات آمنة وفعالة من قبل خبراء.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-57.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "حقن ديسبورت"],
        "hero": {
            "title": "حقن ديسبورت في دبي",
            "subtitle": "ودّع التجاعيد واكشـف عن جمالك الطبيعي",
            "description": "نقدم لك في ايليت بودي هوم علاج الديسبورت، أحد الإجراءات التجميلية الأكثر شهرة لمكافحة التجاعيد، حيث يعمل على تنعيم البشرة ومنحها مظهرًا أكثر شبابًا من خلال تقليل التجاعيد والخطوط الدقيقة. تُعد حقن ديسبورت خيارًا مثاليًا لمن يبحثون عن نتائج سريعة ومظهر طبيعي، دون الحاجة إلى فترة تعافي، مما يجعله الحل الأمثل لإطلالة شبابية متجددة بكل سهولة!"
        },
        "howItWorks": {
            "title": "ما هو حقن ديسبورت؟",
            "description": "يُعد كلٍ من البوتوكس والديسبورت من الطرق غير الجراحية الشائعة لتقليل التجاعيد، حيث يعمل كلاهما على استهداف العضلات المسؤولة عن ظهور الخطوط الدقيقة. ومع ذلك، هناك بعض الفروقات في استخداماتهما المعتمدة. تم ترخيص الديسبورت بشكل خاص لعلاج خطوط العبوس بين الحاجبين. أما البوتوكس، فهو معتمد لعلاج مجموعة أوسع من المناطق، مثل خطوط العبوس بين الحاجبين، وتجاعيد الجبهة، وخطوط حول العينين (أقدام الغراب). ورغم اختلاف المناطق المستهدفة قليلاً، إلا أن كلا العلاجين يعملان على إرخاء العضلات لمنح البشرة مظهرًا أكثر نعومة وشبابًا."
        },
        "procedureSteps": {
            "heading": "خطوات إجراء علاج الديسبورت",
            "para": "يُعد الديسبورت علاجًا غير جراحي يعمل على إرخاء عضلات الوجه المستهدفة لتقليل التجاعيد بشكل مؤقت. تُحقن كمية صغيرة من توكسين البوتولينوم من النوع أ (ديسبورت) في عضلات معينة لمنع انقباضها، مما يساعد على تنعيم سطح الجلد ومنح البشرة مظهرًا أكثر شبابًا.",
            "steps": [
                {
                    "title": "الاستشارة",
                    "desc": "يقوم المختص بتقييم بنية وجهك ويناقش النتائج التي ترغب في تحقيقها.",
                    "icon": "fa-regular fa-comment-dots"
                },
                {
                    "title": "مواقع الحقن",
                    "desc": "تشمل المناطق الشائعة لحقن الديسبورت الجبهة، والتجاعيد حول العينين، وخطوط العبوس بين الحاجبين.",
                    "icon": "fa-regular fa-hand-sparkles"
                },
                {
                    "title": "مدة الجلسة",
                    "desc": "تستغرق الجلسة من 10 إلى 20 دقيقة عادة، مع حد أدنى من الانزعاج بفضل استخدام إبر دقيقة.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "العناية بعد الجلسة",
                    "desc": "قد يحدث احمرار أو تورم خفيف، ولكن عادة ما يختفي خلال بضع ساعات فقط.",
                    "icon": "fa-solid fa-hands"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد حقن ديسبورت",
            "subheading": "",
            "para": "يُعتبر الديسبورت خيارًا مثاليًا لمن يبحثون عن بشرة أكثر نضارة وشبابًا دون الحاجة إلى الجراحة. إليك أبرز الأسباب التي تجعل ديسبورت علاجًا مميزًا للتجاعيد:",
            "items": [
                {
                    "title": "نتائج سريعة",
                    "desc": "تبدأ النتائج بالظهور خلال يومين إلى ثلاثة أيام بعد الإجراء.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "إجراء غير جراحي",
                    "desc": "لا يتطلب حقن الديسبورت تخديرًا، مما يجعلها خيارًا سهلاً وآمنًا.",
                    "icon": "fa-solid fa-hand-holding-medical"
                },
                {
                    "title": "وقت تعافي قصير جدًا",
                    "desc": "يمكنك مباشرة أنشطتك اليومية بعد العلاج دون الحاجة إلى فترة نقاهة.",
                    "icon": "fa-regular fa-face-smile"
                },
                {
                    "title": "مظهر طبيعي",
                    "desc": "يساعد الديسبورت على تنعيم التجاعيد بشكل طبيعي دون منحك مظهرًا متجمّدًا أو غير طبيعي.",
                    "icon": "fa-solid fa-sliders"
                },
                {
                    "title": "نتائج تدوم طويلًا",
                    "desc": "تستمر النتائج لمدة تتراوح بين 3 إلى 4 أشهر، مما يجعله خيارًا فعالًا للعناية الدائمة بالبشرة.",
                    "icon": "fa-regular fa-clock"
                },
                {
                    "title": "تعزيز الثقة بالنفس",
                    "desc": "يمنحك ديسبورت مظهراً أكثر إشراقًا وشبابًا، مما يعزز من ثقتك بنفسك في جميع جوانب حياتك.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "eligibility": {
            "heading": "من هو المرشح المثالي لعلاج الديسبورت؟",
            "subheading": "",
            "para": "يجب معرفة ما إذا كنت مرشحًا مناسبًا لعلاج الديسبورت لتحقيق أفضل النتائج. بشكل عام، يمكن للأشخاص الأصحاء الذين تتراوح أعمارهم بين 18 و65 عامًا التفكير في هذا العلاج.",
            "items": [
                {
                    "title": "الأشخاص الذين يعانون من التجاعيد التعبيرية",
                    "desc": "وهي التجاعيد الناتجة عن حركات الوجه المتكررة مثل العبوس أو التحديق.",
                    "icon": "fa-solid fa-user-check"
                },
                {
                    "title": "الأشخاص الدين يرغبون في حل غير جراحي",
                    "desc": "إذا كنت تتجنب مخاطر الجراحة وفترة التعافي الطويلة، فإن ديسبورت هو الخيار المثالي لك.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "الأشخاص الدين يمتلكون توقعات واقعية",
                    "desc": "على الرغم من أن ديسبورت يقلل بشكل كبير من التجاعيد، فقد لا تختفي جميعها تمامًا، خاصة التجاعيد العميقة جدًا.",
                    "icon": "fa-solid fa-smoking-ban"
                },
                {
                    "title": "غير الحوامل أو المرضعات",
                    "desc": "يجب على النساء الحوامل أو المرضعات تجنب استخدام ديسبورت.",
                    "icon": "fa-solid fa-scale-balanced"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد لعلاج الديسبورت",
            "subheading": "",
            "para": "يجب التحضير الجيد قبل الجلسة لضمان أفضل النتائج من علاج الديسبورت. إليك بعض الخطوات الأساسية التي يجب اتباعها قبل الإجراء:",
            "items": [
                {
                    "title": "تجنب مميعات الدم",
                    "desc": "امتنع عن تناول الأسبرين أو الإيبوبروفين أو الأدوية مميعة للدم لمدة لا تقل عن أسبوع قبل الجلسة للتقليل من احتمالية الكدمات.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "حافظ على الترطيب",
                    "desc": "اشرب كمية كافية من الماء خلال الأيام التي تسبق جلستك للحفاظ على بشرة صحية ورطبة.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "تقليل شرب الكحول",
                    "desc": "يُنصح بالامتناع عن تناول الكحول لمدة 24 ساعة قبل العلاج للتقليل من خطر التورم والكدمات.",
                    "icon": "fa-solid fa-wine-glass-empty"
                },
                {
                    "title": "إبلاغ الطبيب بالأدوية أو المكملات الغذائية",
                    "desc": "قد تؤثر بعض الأدوية أو المكملات الغذائية على الإجراء، لذا من الضروري إبلاغ الطبيب بالأدوية أو المكملات الغذاية التي تتناولها.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "الحضور ببشرة نظيفة",
                    "desc": "تأكد من إزالة المكياج أو الكريمات من وجهك في يوم الجلسة لضمان أفضل امتصاص للمادة الفعالة.",
                    "icon": "fa-solid fa-soap"
                }
            ]
        },
        "results": {
            "heading": "نتائج علاج ديسبورت بوتوكس",
            "subheading": "",
            "para": "تعمل حقن ديسبورت على تقليل التجاعيد التعبيرية، مما يمنح البشرة مظهراً طبيعيًا وأكثر نعومة. ويُمّكن المرضى من التمتع بإطلالة أكثر شباباً ونضارة بعد العلاج دون الانقطاع عن أنشطتهم اليومية.",
            "items": [
                {
                    "title": "بشرة أكثر نعومة",
                    "desc": "تحسن ملحوظ في مظهر التجاعيد والخطوط الدقيقة، مما يجعل البشرة تبدو أكثر إشراقاً وحيوية.",
                    "icon": "fa-solid fa-gauge-high"
                },
                {
                    "title": "مظهرًا طبيعيًا",
                    "desc": "تعمل العضلات المسترخية على إضفاء لمسة شبابية دون التأثير على تعابير الوجه الطبيعية، مما يمنحك نتائج متوازنة وغير مبالغ فيها.",
                    "icon": "fa-solid fa-check-double"
                },
                {
                    "title": "نتائج تدوم طويلاً",
                    "desc": "تدوم تأثيرات ديسبورت عادةً من 3 إلى 4 أشهر، مما يجعله حلاً عملياً للحفاظ على بشرة ناعمة وخالية من التجاعيد.",
                    "icon": "fa-regular fa-hourglass-half"
                },
                {
                    "title": "تعزيز الثقة بالنفس",
                    "desc": "يشعر العديد من المرضى بعد العلاج بمزيد من الثقة والرضا عن مظهرهم، مما ينعكس إيجابياً على حياتهم الشخصية والمهنية.",
                    "icon": "fa-solid fa-bed"
                }
            ]
        },
        "aftercareTips": {
            "heading": "نصائح العناية بعد العلاج",
            "subheading": "",
            "para": "اتباع هذه الإرشادات يساعد في تقليل الآثار الجانبية وتحقيق أفضل النتائج من العلاج.",
            "items": [
                {
                    "title": "تجنب لمس المنطقة المعالجة",
                    "desc": "تجنب لمس أو تدليك المنطقة المعالجة لمدة 24 ساعة على الأقل بعد الإجراء.",
                    "icon": "fa-solid fa-hand-paper"
                },
                {
                    "title": "تجنب النشاط البدني المكثف",
                    "desc": "امتنع عن ممارسة التمارين الشاقة لمدة 24 إلى 48 ساعة بعد العلاج لتقليل خطر التورم أو الكدمات.",
                    "icon": "fa-solid fa-person-walking"
                },
                {
                    "title": "الحفاظ على الترطيب",
                    "desc": "اشرب الكثير من الماء للمساعدة في ترطيب بشرتك ومساعدة المادة الفعالة على الاستقرار بشكل طبيعي.",
                    "icon": "fa-solid fa-droplet"
                },
                {
                    "title": "تجنب التعرض للحرارة",
                    "desc": "تجنب الساونا وغرف البخار والتعرض المباشر لأشعة الشمس لبضعة أيام، حيث يمكن أن تزيد الحرارة من التورم والتهيج.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "استخدام الكمادات الباردة",
                    "desc": "في حالة وجود تورم أو كدمات طفيفة (وهو أمر طبيعي)، ضع كمادة باردة برفق على المنطقة لمدة 10-15 دقيقة لتخفيف الالتهاب.",
                    "icon": "fa-solid fa-snowflake"
                },
                {
                    "title": "النوم مع رفع الرأس",
                    "desc": "في الليلة الأولى أو الثانية، نم ورأسك مرتفع قليلاً لتقليل التورم ولمنع أي حركة للمادة الفعالة.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "تجنب الكحول ومميعات الدم",
                    "desc": "تجنب الكحول ومميعات الدم مثل الأسبرين لمدة 24 ساعة على الأقل بعد الإجراء لتقليل الكدمات.",
                    "icon": "fa-solid fa-ban"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "إذا كنت تفكر في علاج ديسبورت في دبي، فإليك إجابات عن الأسئلة الشائعة حول الإجراء والنتائج.",
            "items": [
                {
                    "question": "كم من الوقت يستغرق ظهور النتائج؟",
                    "answer": "يمكن رؤية النتائج في غضون 2-3 أيام بعد العلاج."
                },
                {
                    "question": "هل ديسبورت آمن؟",
                    "answer": "نعم، ديسبورت حاصل على موافقة إدارة الغذاء والدواء (FDA) وقد تم استخدامه بأمان في العلاجات التجميلية لسنوات."
                },
                {
                    "question": "كم مرة يجب أن أحصل على حقن ديسبورت؟",
                    "answer": "معظم المرضى يفضلون الخضوع للعلاج كل 4-6 أشهر للحفاظ على النتائج."
                }
            ]
        }
        ,
        "treatmentAreas1": [
            {
                "ar": "خطوط العبوس بين الحاجبين",
                "title": "خطوط العبوس بين الحاجبين",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Forehead-Lines.jpg"
            },
            {
                "ar": "تجاعيد الجبهة",
                "title": "تجاعيد الجبهة",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Frown-Lines.jpg"
            }
        ],
        "treatmentAreas2": []
    }

};
